import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { fetchOrder, updateOrderStatus, syncOrderFromPicqer, toggleSyncLock, replaceItemProduct } from '../store/slices/ordersSlice';
import api from '../services/api';

const StatusBadge = ({ status }) => {
  const statusColors = {
    new: 'primary',
    in_queue: 'info',
    assigned: 'secondary',
    in_production: 'warning',
    quality_check: 'info',
    ready_for_shipping: 'success',
    shipped: 'success',
    on_hold: 'danger',
    cancelled: 'dark'
  };

  const statusLabels = {
    new: 'Nieuw',
    in_queue: 'In wachtrij',
    assigned: 'Toegewezen',
    in_production: 'In productie',
    quality_check: 'Quality check',
    ready_for_shipping: 'Gereed',
    shipped: 'Verzonden',
    on_hold: 'On hold',
    cancelled: 'Geannuleerd'
  };

  return (
    <span className={`badge bg-${statusColors[status] || 'secondary'}`}>
      {statusLabels[status] || status}
    </span>
  );
};

const OrderDetail = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { currentOrder: order, loading, error } = useSelector((state) => state.orders);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [statusNote, setStatusNote] = useState('');
  const [syncing, setSyncing] = useState(false);

  // Product replace modal state
  const [showProductModal, setShowProductModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [productSearch, setProductSearch] = useState('');
  const [productResults, setProductResults] = useState([]);
  const [searchingProducts, setSearchingProducts] = useState(false);
  const [replacing, setReplacing] = useState(false);

  // Product detail modal state
  const [showProductDetailModal, setShowProductDetailModal] = useState(false);
  const [productDetail, setProductDetail] = useState(null);
  const [loadingProduct, setLoadingProduct] = useState(false);

  const openProductDetail = async (sku) => {
    if (!sku) return;
    setShowProductDetailModal(true);
    setProductDetail(null);
    setLoadingProduct(true);
    try {
      const response = await api.get('/products', { params: { search: sku, limit: 1 } });
      const products = response.data.products || [];
      const match = products.find(p => p.productCode === sku) || products[0];
      setProductDetail(match || null);
    } catch (err) {
      console.error('Product ophalen mislukt:', err);
    } finally {
      setLoadingProduct(false);
    }
  };

  useEffect(() => {
    dispatch(fetchOrder(id));
  }, [dispatch, id]);

  const handleStatusChange = () => {
    dispatch(updateOrderStatus({ id, status: newStatus, note: statusNote }));
    setShowStatusModal(false);
    setStatusNote('');
  };

  const handleSyncFromPicqer = async () => {
    if (!order?.picqerId) return;
    setSyncing(true);
    try {
      await dispatch(syncOrderFromPicqer(order.picqerId)).unwrap();
    } catch (err) {
      console.error('Sync failed:', err);
    } finally {
      setSyncing(false);
    }
  };

  const handleToggleSyncLock = () => {
    dispatch(toggleSyncLock(id));
  };

  // Debounced product search
  const searchProducts = useCallback(async (query) => {
    if (!query || query.length < 2) {
      setProductResults([]);
      return;
    }
    setSearchingProducts(true);
    try {
      const response = await api.get('/products', { params: { search: query, limit: 10 } });
      setProductResults(response.data.products || []);
    } catch (err) {
      console.error('Product search failed:', err);
      setProductResults([]);
    } finally {
      setSearchingProducts(false);
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      searchProducts(productSearch);
    }, 300);
    return () => clearTimeout(timer);
  }, [productSearch, searchProducts]);

  const openProductModal = (item) => {
    setEditingItem(item);
    setProductSearch('');
    setProductResults([]);
    setShowProductModal(true);
  };

  const handleReplaceProduct = async (product) => {
    if (!editingItem) return;
    setReplacing(true);
    try {
      await dispatch(replaceItemProduct({
        orderId: id,
        itemId: editingItem._id,
        productId: product._id
      })).unwrap();
      setShowProductModal(false);
      setEditingItem(null);
    } catch (err) {
      console.error('Replace failed:', err);
    } finally {
      setReplacing(false);
    }
  };

  const statuses = [
    { value: 'new', label: 'Nieuw' },
    { value: 'in_queue', label: 'In wachtrij' },
    { value: 'assigned', label: 'Toegewezen' },
    { value: 'in_production', label: 'In productie' },
    { value: 'ready_for_shipping', label: 'Gereed voor verzending' },
    { value: 'shipped', label: 'Verzonden' },
    { value: 'on_hold', label: 'On hold' },
    { value: 'cancelled', label: 'Geannuleerd' }
  ];

  if (loading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Laden...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-danger">
        {error}
        <button className="btn btn-link" onClick={() => navigate('/orders')}>
          Terug naar orders
        </button>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="alert alert-warning">
        Order niet gevonden
        <Link to="/orders" className="btn btn-link">Terug naar orders</Link>
      </div>
    );
  }

  return (
    <div className="order-detail">
      {/* Header */}
      <div className="page-header d-flex justify-content-between align-items-center">
        <div>
          <nav aria-label="breadcrumb">
            <ol className="breadcrumb">
              <li className="breadcrumb-item">
                <Link to="/orders">Orders</Link>
              </li>
              <li className="breadcrumb-item active">{order.orderNumber}</li>
            </ol>
          </nav>
          <h1>Order {order.orderNumber}</h1>
        </div>
        <div className="d-flex gap-2">
          {order.picqerId && (
            <button
              className="btn btn-outline-secondary"
              onClick={handleSyncFromPicqer}
              disabled={syncing || order.syncLocked}
              title={order.syncLocked ? 'Sync is geblokkeerd' : ''}
            >
              {syncing ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                  Synchroniseren...
                </>
              ) : (
                <>
                  <i className={`bi ${order.syncLocked ? 'bi-lock' : 'bi-arrow-repeat'} me-2`}></i>
                  Sync van Picqer
                </>
              )}
            </button>
          )}
          <button
            className="btn btn-outline-primary"
            onClick={() => {
              setNewStatus(order.status);
              setShowStatusModal(true);
            }}
          >
            <i className="bi bi-pencil me-2"></i>
            Status wijzigen
          </button>
        </div>
      </div>

      <div className="row">
        {/* Order Info */}
        <div className="col-lg-8">
          <div className="card mb-4">
            <div className="card-header d-flex justify-content-between align-items-center">
              <h5 className="mb-0">Order Items</h5>
            </div>
            <div className="card-body p-0">
              <div className="table-responsive">
                <table className="table mb-0">
                  <thead>
                    <tr>
                      <th>Product</th>
                      <th>SKU</th>
                      <th>Afmetingen</th>
                      <th>Materiaal</th>
                      <th>Aantal</th>
                      <th>Status</th>
                      <th style={{ width: '40px' }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {order.items?.map((item, index) => (
                      <tr key={index}>
                        <td>
                          <div className="fw-medium">
                            <a
                              href="#"
                              className="text-decoration-none text-dark"
                              onClick={(e) => { e.preventDefault(); openProductDetail(item.sku); }}
                              style={{ borderBottom: '1px dashed #6c757d' }}
                            >
                              {item.productName}
                            </a>
                          </div>
                          {item.originalProduct?.sku && (
                            <div className="mt-1">
                              <small className="text-warning">
                                <i className="bi bi-arrow-left-right me-1"></i>
                                Was: {item.originalProduct.productName} ({item.originalProduct.sku})
                              </small>
                            </div>
                          )}
                          {item.shape === 'custom' && item.shapeData && (
                            <span className="badge bg-warning text-dark me-1">
                              {(() => {
                                try {
                                  const data = JSON.parse(item.shapeData);
                                  return data.formType || 'Custom';
                                } catch { return 'Custom'; }
                              })()}
                            </span>
                          )}
                          {item.notes && (
                            <div className="mt-1">
                              <small className="text-muted" style={{ whiteSpace: 'pre-line' }}>
                                {item.notes}
                              </small>
                            </div>
                          )}
                        </td>
                        <td><code>{item.sku}</code></td>
                        <td>
                          {(() => {
                            const pr = item.parsedRemarks || {};
                            // parsedRemarks values are raw cm from Picqer
                            const rawWidth = pr['Breedte (in cm)'] || pr['Breedte'];
                            const rawHeight = pr['Lengte (in cm)'] || pr['Lengte'];
                            if (rawWidth && rawHeight) {
                              return `${rawWidth} x ${rawHeight} cm`;
                            }
                            // Fallback to stored dimensions (in mm)
                            if (item.dimensions?.width && item.dimensions?.height) {
                              return `${item.dimensions.width} x ${item.dimensions.height} ${item.dimensions?.unit || 'mm'}`;
                            }
                            return '-';
                          })()}
                        </td>
                        <td>
                          <span className="badge bg-light text-dark">
                            {item.materialType || '-'}
                          </span>
                        </td>
                        <td>{item.quantity}</td>
                        <td>
                          {(() => {
                            const itemStatusColors = {
                              pending: 'secondary',
                              in_production: 'warning',
                              completed: 'success',
                              error: 'danger'
                            };
                            const itemStatusLabels = {
                              pending: 'Wacht',
                              in_production: 'In productie',
                              completed: 'Voltooid',
                              error: 'Fout'
                            };
                            const s = item.status || 'pending';
                            return (
                              <span className={`badge bg-${itemStatusColors[s] || 'secondary'}`}>
                                {itemStatusLabels[s] || s}
                              </span>
                            );
                          })()}
                        </td>
                        <td>
                          <button
                            className="btn btn-sm btn-outline-secondary"
                            onClick={() => openProductModal(item)}
                            title="Product vervangen"
                          >
                            <i className="bi bi-arrow-left-right"></i>
                          </button>
                        </td>
                      </tr>
                    )) || (
                      <tr>
                        <td colSpan="7" className="text-center text-muted py-4">
                          Geen items
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Status History */}
          <div className="card">
            <div className="card-header">
              <h5 className="mb-0">Status Geschiedenis</h5>
            </div>
            <div className="card-body">
              {order.statusHistory?.length > 0 ? (
                <div className="timeline">
                  {order.statusHistory.map((entry, index) => (
                    <div key={index} className="timeline-item">
                      <div className="timeline-marker"></div>
                      <div className="timeline-content">
                        <div className="d-flex justify-content-between">
                          <StatusBadge status={entry.status} />
                          <small className="text-muted">
                            {new Date(entry.timestamp).toLocaleString('nl-NL')}
                          </small>
                        </div>
                        {entry.note && (
                          <p className="text-muted small mb-0 mt-1">{entry.note}</p>
                        )}
                        {entry.user && (
                          <small className="text-muted">Door: {entry.user}</small>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-muted text-center py-3">Geen status geschiedenis</p>
              )}
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="col-lg-4">
          {/* Status Card */}
          <div className="card mb-4">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <span className="text-muted">Status</span>
                <StatusBadge status={order.status} />
              </div>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <span className="text-muted">Prioriteit</span>
                <span className={`badge bg-${order.priority >= 8 ? 'danger' : order.priority >= 5 ? 'warning' : 'secondary'}`}>
                  {order.priority >= 8 ? 'Spoed' : order.priority >= 5 ? 'Hoog' : 'Normaal'}
                </span>
              </div>
              <div className="d-flex justify-content-between align-items-center">
                <span className="text-muted">Aangemaakt</span>
                <span>{new Date(order.createdAt).toLocaleDateString('nl-NL')}</span>
              </div>
            </div>
          </div>

          {/* Customer Card */}
          <div className="card mb-4">
            <div className="card-header">
              <h6 className="mb-0">Klantgegevens</h6>
            </div>
            <div className="card-body">
              <p className="mb-1"><strong>{order.customer?.name || '-'}</strong></p>
              {order.customer?.email && (
                <p className="mb-1 small text-muted">{order.customer.email}</p>
              )}
              {order.customer?.phone && (
                <p className="mb-0 small text-muted">{order.customer.phone}</p>
              )}
            </div>
          </div>

          {/* Shipping Address */}
          {order.deliveryAddress && (
            <div className="card mb-4">
              <div className="card-header">
                <h6 className="mb-0">Verzendadres</h6>
              </div>
              <div className="card-body">
                {order.deliveryAddress.street && (
                  <p className="mb-1">{order.deliveryAddress.street}</p>
                )}
                <p className="mb-1">
                  {order.deliveryAddress.postalCode} {order.deliveryAddress.city}
                </p>
                {order.deliveryAddress.country && (
                  <p className="mb-0">{order.deliveryAddress.country}</p>
                )}
              </div>
            </div>
          )}

          {/* Picqer Info */}
          {order.picqerId && (
            <div className="card">
              <div className="card-header">
                <h6 className="mb-0">Picqer</h6>
              </div>
              <div className="card-body">
                <p className="mb-1">
                  <span className="text-muted">Picqer ID:</span> {order.picqerId}
                </p>
                {order.metadata?.picqerStatus && (
                  <p className="mb-3">
                    <span className="text-muted">Picqer Status:</span> {order.metadata.picqerStatus}
                  </p>
                )}
                <div className="form-check form-switch">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    role="switch"
                    id="syncLockSwitch"
                    checked={order.syncLocked || false}
                    onChange={handleToggleSyncLock}
                  />
                  <label className="form-check-label" htmlFor="syncLockSwitch">
                    Sync blokkeren
                  </label>
                </div>
                {order.syncLocked && (
                  <div className="alert alert-warning mt-2 mb-0 py-2 px-3 small">
                    <i className="bi bi-lock me-1"></i>
                    Deze order wordt niet overschreven bij sync met Picqer.
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Status Modal */}
      {showStatusModal && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Status wijzigen</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowStatusModal(false)}
                ></button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label">Nieuwe status</label>
                  <select
                    className="form-select"
                    value={newStatus}
                    onChange={(e) => setNewStatus(e.target.value)}
                  >
                    {statuses.map((status) => (
                      <option key={status.value} value={status.value}>
                        {status.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="mb-3">
                  <label className="form-label">Notitie (optioneel)</label>
                  <textarea
                    className="form-control"
                    rows="3"
                    value={statusNote}
                    onChange={(e) => setStatusNote(e.target.value)}
                    placeholder="Voeg een notitie toe..."
                  ></textarea>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowStatusModal(false)}
                >
                  Annuleren
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleStatusChange}
                >
                  Opslaan
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Product Detail Modal */}
      {showProductDetailModal && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }} onClick={() => setShowProductDetailModal(false)}>
          <div className="modal-dialog modal-lg" onClick={(e) => e.stopPropagation()}>
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {loadingProduct ? 'Product laden...' : productDetail ? productDetail.name : 'Product niet gevonden'}
                </h5>
                <button type="button" className="btn-close" onClick={() => setShowProductDetailModal(false)}></button>
              </div>
              <div className="modal-body">
                {loadingProduct ? (
                  <div className="text-center py-4">
                    <div className="spinner-border text-primary" role="status"></div>
                  </div>
                ) : productDetail ? (
                  <div className="row">
                    <div className="col-md-7">
                      {/* Product info */}
                      <div className="mb-3">
                        <code className="fs-6">{productDetail.productCode}</code>
                      </div>
                      <div className="row mb-3">
                        <div className="col-6">
                          <small className="text-muted d-block">Materiaal</small>
                          <span className="badge bg-primary">{productDetail.materialType || '-'}</span>
                        </div>
                        <div className="col-6">
                          <small className="text-muted d-block">Rolbreedte</small>
                          <span className="fw-medium">{productDetail.rollWidth ? `${productDetail.rollWidth} cm` : '-'}</span>
                        </div>
                      </div>
                      {productDetail.description && (
                        <div className="mb-3">
                          <small className="text-muted d-block">Beschrijving</small>
                          <p className="mb-0">{productDetail.description}</p>
                        </div>
                      )}

                      {/* Parts */}
                      {productDetail.parts?.length > 0 && (
                        <div className="mb-3">
                          <small className="text-muted d-block mb-1">Onderdelen ({productDetail.parts.length})</small>
                          <div className="table-responsive">
                            <table className="table table-sm mb-0">
                              <thead>
                                <tr>
                                  <th>Code</th>
                                  <th>Naam</th>
                                  <th>Aantal</th>
                                </tr>
                              </thead>
                              <tbody>
                                {productDetail.parts.map((part, i) => (
                                  <tr key={i}>
                                    <td><code>{part.productcode}</code></td>
                                    <td>{part.name}</td>
                                    <td>{part.amount || 1}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}

                      {/* Productfields */}
                      {productDetail.metadata?.picqerData?.productfields?.length > 0 && (
                        <div>
                          <small className="text-muted d-block mb-1">Picqer velden</small>
                          <div className="table-responsive">
                            <table className="table table-sm mb-0">
                              <tbody>
                                {productDetail.metadata.picqerData.productfields
                                  .filter(f => f.value)
                                  .map((field, i) => (
                                    <tr key={i}>
                                      <td className="text-muted">{field.title}</td>
                                      <td className="fw-medium">{field.value}</td>
                                    </tr>
                                  ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="col-md-5">
                      {/* Stock */}
                      <div className="card mb-3">
                        <div className="card-header py-2">
                          <h6 className="mb-0">Voorraad</h6>
                        </div>
                        <div className="card-body py-2">
                          <div className="d-flex justify-content-between">
                            <span className="text-muted">Beschikbaar</span>
                            <span className="fw-medium">{productDetail.stock?.quantity || 0}</span>
                          </div>
                          <div className="d-flex justify-content-between">
                            <span className="text-muted">Gereserveerd</span>
                            <span className="fw-medium">{productDetail.stock?.reserved || 0}</span>
                          </div>
                        </div>
                      </div>

                      {/* Stock locations */}
                      {productDetail.stockLocations?.length > 0 && (
                        <div className="card mb-3">
                          <div className="card-header py-2">
                            <h6 className="mb-0">Locaties</h6>
                          </div>
                          <div className="card-body p-0">
                            <table className="table table-sm mb-0">
                              <tbody>
                                {productDetail.stockLocations.map((loc, i) => (
                                  <tr key={i}>
                                    <td>
                                      {loc.name}
                                      {loc.isBulkLocation && <span className="badge bg-info ms-1" style={{ fontSize: '0.6em' }}>Bulk</span>}
                                    </td>
                                    <td className="text-end">{loc.freeStock}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}

                      {/* Machines */}
                      <div className="card">
                        <div className="card-header py-2">
                          <h6 className="mb-0">Machines</h6>
                        </div>
                        <div className="card-body py-2">
                          {productDetail.primaryMachine && (
                            <div className="mb-2">
                              <small className="text-muted d-block">Primair</small>
                              <span className="badge bg-primary">
                                {productDetail.primaryMachine.name || productDetail.primaryMachine}
                              </span>
                            </div>
                          )}
                          {productDetail.compatibleMachines?.length > 0 && (
                            <div>
                              <small className="text-muted d-block">Compatibel</small>
                              <div className="d-flex flex-wrap gap-1">
                                {productDetail.compatibleMachines.map((m, i) => (
                                  <span key={i} className="badge bg-secondary">{m.name || m}</span>
                                ))}
                              </div>
                            </div>
                          )}
                          {!productDetail.primaryMachine && !productDetail.compatibleMachines?.length && (
                            <span className="text-muted">Geen machines</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-4 text-muted">
                    Product niet gevonden in de database
                  </div>
                )}
              </div>
              <div className="modal-footer">
                {productDetail && (
                  <Link to={`/products/${productDetail._id}`} className="btn btn-outline-primary me-auto">
                    <i className="bi bi-box-arrow-up-right me-1"></i>
                    Volledige pagina
                  </Link>
                )}
                <button type="button" className="btn btn-secondary" onClick={() => setShowProductDetailModal(false)}>
                  Sluiten
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Product Replace Modal */}
      {showProductModal && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Product vervangen</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => { setShowProductModal(false); setEditingItem(null); }}
                ></button>
              </div>
              <div className="modal-body">
                {editingItem && (
                  <div className="alert alert-light mb-3">
                    <small className="text-muted">Huidig product:</small>
                    <div className="fw-medium">{editingItem.productName} <code className="ms-2">{editingItem.sku}</code></div>
                    {editingItem.originalProduct?.sku && (
                      <small className="text-warning">
                        <i className="bi bi-info-circle me-1"></i>
                        Origineel: {editingItem.originalProduct.productName} ({editingItem.originalProduct.sku})
                      </small>
                    )}
                  </div>
                )}
                <div className="mb-3">
                  <label className="form-label">Zoek nieuw product</label>
                  <div className="input-group">
                    <span className="input-group-text"><i className="bi bi-search"></i></span>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Zoek op naam of productcode..."
                      value={productSearch}
                      onChange={(e) => setProductSearch(e.target.value)}
                      autoFocus
                    />
                  </div>
                </div>
                <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                  {searchingProducts ? (
                    <div className="text-center py-3">
                      <div className="spinner-border spinner-border-sm text-primary" role="status"></div>
                      <span className="ms-2 text-muted">Zoeken...</span>
                    </div>
                  ) : productResults.length > 0 ? (
                    <div className="list-group">
                      {productResults.map((product) => (
                        <button
                          key={product._id}
                          className="list-group-item list-group-item-action d-flex justify-content-between align-items-center"
                          onClick={() => handleReplaceProduct(product)}
                          disabled={replacing}
                        >
                          <div>
                            <div className="fw-medium">{product.name}</div>
                            <small className="text-muted">
                              <code>{product.productCode}</code>
                              {product.materialType && (
                                <span className="badge bg-light text-dark ms-2">{product.materialType}</span>
                              )}
                            </small>
                          </div>
                          <i className="bi bi-arrow-right text-primary"></i>
                        </button>
                      ))}
                    </div>
                  ) : productSearch.length >= 2 ? (
                    <p className="text-muted text-center py-3">Geen producten gevonden</p>
                  ) : (
                    <p className="text-muted text-center py-3">Typ minimaal 2 tekens om te zoeken</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderDetail;
